import React, { useState, useEffect, useCallback } from 'react';
import { Spinner, Alert, Badge, Button, Form } from 'react-bootstrap';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import api from './api';

// Trend verisini günlük harita olarak birleştir (eksik günleri 0 ile doldur)
function trendleriEslestir(kullanici, danisan, randevu, periyot) {
  const bugun = new Date();
  const gunler = [];
  for (let i = periyot - 1; i >= 0; i--) {
    const d = new Date(bugun);
    d.setDate(d.getDate() - i);
    gunler.push(d.toISOString().slice(0, 10));
  }

  const kullaniciMap = Object.fromEntries((kullanici || []).map(r => [r.tarih, r.sayi]));
  const danisanMap = Object.fromEntries((danisan || []).map(r => [r.tarih, r.sayi]));
  const randevuMap = Object.fromEntries((randevu || []).map(r => [r.tarih, r.sayi]));

  return gunler.map(gun => ({
    tarih: gun.slice(5), // MM-DD
    'Yeni Kullanıcı': kullaniciMap[gun] || 0,
    'Yeni Danışan': danisanMap[gun] || 0,
    'Randevu': randevuMap[gun] || 0,
  }));
}

function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [demoHesaplar, setDemoHesaplar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analitik, setAnalitik] = useState(null);
  const [periyot, setPeriyot] = useState(7);
  const [analitikYukleniyor, setAnalitikYukleniyor] = useState(false);

  // Karşılama sayfası trafiği (bağımsız yüklenir; hata olursa dashboard çalışmaya devam eder)
  const [trafik, setTrafik] = useState(null);

  // Sunucu uyanık tutma (ısındırma) ayarı
  const [isindirma, setIsindirma] = useState(null); // {aktif, baslangic_saat, bitis_saat, sunucu_destegi}
  const [isindirmaMsg, setIsindirmaMsg] = useState('');
  const [isindirmaKaydediliyor, setIsindirmaKaydediliyor] = useState(false);

  const verileriGetir = useCallback(() => {
    Promise.all([
      api.get('/admin/istatistikler'),
      api.get('/admin/demo-hesaplar')
    ])
      .then(([statsRes, demoRes]) => {
        setStats(statsRes.data);
        setDemoHesaplar(demoRes.data);
      })
      .catch(err => console.error("Veriler yüklenemedi", err))
      .finally(() => setLoading(false));
  }, []);

  const analitikGetir = useCallback(async (p) => {
    setAnalitikYukleniyor(true);
    try {
      const res = await api.get(`/admin/analitik?periyot=${p}`);
      setAnalitik(res.data);
    } catch (e) {
      console.error('Analitik yüklenemedi', e);
    } finally {
      setAnalitikYukleniyor(false);
    }
  }, []);

  useEffect(() => {
    verileriGetir();
    api.get('/admin/ayarlar/isindirma')
      .then(res => setIsindirma(res.data))
      .catch(() => setIsindirma(null));
    api.get('/admin/ziyaret-istatistik')
      .then(res => setTrafik(res.data))
      .catch(() => setTrafik(null));
  }, [verileriGetir]);

  const handleIsindirmaKaydet = async (yeniAyar) => {
    setIsindirmaKaydediliyor(true);
    setIsindirmaMsg('');
    try {
      const res = await api.put('/admin/ayarlar/isindirma', {
        aktif: yeniAyar.aktif,
        baslangic_saat: yeniAyar.baslangic_saat,
        bitis_saat: yeniAyar.bitis_saat,
      });
      setIsindirma(prev => ({ ...prev, ...yeniAyar }));
      setIsindirmaMsg(res.data.mesaj);
    } catch (err) {
      setIsindirmaMsg(err.response?.data?.mesaj || 'Ayar kaydedilemedi.');
    } finally {
      setIsindirmaKaydediliyor(false);
    }
  };

  useEffect(() => {
    analitikGetir(periyot);
  }, [periyot, analitikGetir]);

  const handleDemoOnayla = async (userId) => {
    try {
      await api.post(`/admin/demo-hesaplar/${userId}/onayla`, { sure_ay: 1 });
      verileriGetir();
    } catch (err) {
      alert(err.response?.data?.mesaj || 'Hesap dönüştürülemedi.');
    }
  };

  if (loading) return <Spinner animation="border" />;
  if (!stats) return <Alert variant="warning">Pano verileri yüklenemedi.</Alert>;

  return (
    <div>
      <div className="mb-4">
        <h1 className="display-6 fw-bolder m-0" style={{ color: 'var(--text-main)', letterSpacing: '-1px' }}>Genel Bakış</h1>
        <p className="m-0 mt-1" style={{ color: 'var(--text-muted)' }}>Sistem istatistikleri ve demo hesap yönetimi.</p>
      </div>

      {/* Sunucu Uyanık Tutma Ayarı */}
      {isindirma && (
        <div className="mb-4 p-4" style={{ backgroundColor: 'var(--bg-secondary)', borderRadius: '1rem', boxShadow: 'var(--card-shadow)', border: isindirma.aktif ? '1px solid var(--accent-primary)' : '1px solid var(--border-color)' }}>
          <div className="d-flex flex-wrap align-items-center gap-3 justify-content-between">
            <div className="d-flex align-items-center gap-3" style={{ minWidth: '260px' }}>
              <span className="material-symbols-outlined" style={{ fontSize: '2rem', color: isindirma.aktif ? 'var(--accent-primary)' : 'var(--text-muted)' }}>
                {isindirma.aktif ? 'bolt' : 'mode_night'}
              </span>
              <div>
                <h5 className="m-0 fw-bold" style={{ color: 'var(--text-main)' }}>Sunucuyu Uyanık Tut</h5>
                <small style={{ color: 'var(--text-muted)' }}>
                  Seçilen saatlerde (TR) sunucu uyumaz; site beklemeden açılır.
                </small>
              </div>
            </div>

            <div className="d-flex flex-wrap align-items-center gap-3">
              <div className="d-flex align-items-center gap-2">
                <Form.Select size="sm" value={isindirma.baslangic_saat} disabled={isindirmaKaydediliyor}
                  onChange={e => handleIsindirmaKaydet({ ...isindirma, baslangic_saat: parseInt(e.target.value) })}
                  style={{ width: '90px', backgroundColor: 'var(--bg-primary)', color: 'var(--text-main)', borderColor: 'var(--border-color)' }}>
                  {Array.from({ length: 24 }, (_, i) => <option key={i} value={i}>{String(i).padStart(2, '0')}:00</option>)}
                </Form.Select>
                <span style={{ color: 'var(--text-muted)' }}>—</span>
                <Form.Select size="sm" value={isindirma.bitis_saat} disabled={isindirmaKaydediliyor}
                  onChange={e => handleIsindirmaKaydet({ ...isindirma, bitis_saat: parseInt(e.target.value) })}
                  style={{ width: '90px', backgroundColor: 'var(--bg-primary)', color: 'var(--text-main)', borderColor: 'var(--border-color)' }}>
                  {Array.from({ length: 24 }, (_, i) => <option key={i + 1} value={i + 1}>{String(i + 1).padStart(2, '0')}:00</option>)}
                </Form.Select>
              </div>
              <Form.Check
                type="switch"
                id="isindirma-switch"
                checked={isindirma.aktif}
                disabled={isindirmaKaydediliyor}
                onChange={e => handleIsindirmaKaydet({ ...isindirma, aktif: e.target.checked })}
                label={<span style={{ color: isindirma.aktif ? 'var(--accent-primary)' : 'var(--text-muted)', fontWeight: 'bold' }}>{isindirma.aktif ? 'Açık' : 'Kapalı'}</span>}
              />
            </div>
          </div>
          {!isindirma.sunucu_destegi && (
            <Alert variant="warning" className="mt-3 mb-0 py-2" style={{ fontSize: '0.82rem' }}>
              Sunucu tarafında <code>RENDER_EXTERNAL_URL</code> / <code>KEEP_ALIVE_URL</code> tanımlı görünmüyor — ayar kaydedilir ama ping atılamaz. Render'da bu değişken normalde otomatik gelir.
            </Alert>
          )}
          {isindirmaMsg && (
            <div className="mt-2" style={{ fontSize: '0.82rem', color: 'var(--accent-primary)' }}>{isindirmaMsg}</div>
          )}
          <div className="mt-2" style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            Not: Uyuyan sunucu kendini uyandıramaz — sabah ilk açılış yine ilk ziyaretçiyle olur, sonrasında gün boyu uyanık kalır.
            Bu özellik Render'ın ücretsiz saat kotasından kullanır (8 saat/gün ≈ ayda 240 saat, 750 saatlik kotanın içinde).
          </div>
        </div>
      )}

      {/* İstatistik Kartları */}
      <div className="mb-5" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem' }}>
        <div className="p-4 position-relative overflow-hidden" style={{ backgroundColor: 'var(--bg-secondary)', borderRadius: '1rem', boxShadow: 'var(--card-shadow)' }}>
          <div className="position-absolute opacity-25" style={{ top: '-10px', right: '-10px', color: 'var(--accent-primary)' }}>
            <span className="material-symbols-outlined" style={{ fontSize: '80px' }}>badge</span>
          </div>
          <div className="position-relative">
            <div style={{ fontSize: '0.7rem', fontWeight: 'bold', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '1px' }}>Diyetisyen</div>
            <div className="display-5 fw-bolder mt-1" style={{ color: 'var(--text-main)' }}>{stats.diyetisyen_sayisi}</div>
          </div>
        </div>
        <div className="p-4 position-relative overflow-hidden" style={{ backgroundColor: 'var(--bg-secondary)', borderRadius: '1rem', boxShadow: 'var(--card-shadow)' }}>
          <div className="position-absolute opacity-25" style={{ top: '-10px', right: '-10px', color: 'var(--accent-primary)' }}>
            <span className="material-symbols-outlined" style={{ fontSize: '80px' }}>group</span>
          </div>
          <div className="position-relative">
            <div style={{ fontSize: '0.7rem', fontWeight: 'bold', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '1px' }}>Danışan</div>
            <div className="display-5 fw-bolder mt-1" style={{ color: 'var(--text-main)' }}>{stats.toplam_danisan_sayisi}</div>
          </div>
        </div>
        <div className="p-4 position-relative overflow-hidden" style={{ backgroundColor: 'var(--bg-secondary)', borderRadius: '1rem', boxShadow: 'var(--card-shadow)' }}>
          <div className="position-absolute opacity-25" style={{ top: '-10px', right: '-10px', color: 'var(--accent-tertiary)' }}>
            <span className="material-symbols-outlined" style={{ fontSize: '80px' }}>inventory_2</span>
          </div>
          <div className="position-relative">
            <div style={{ fontSize: '0.7rem', fontWeight: 'bold', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '1px' }}>Paket</div>
            <div className="display-5 fw-bolder mt-1" style={{ color: 'var(--text-main)' }}>{stats.paket_sayisi}</div>
          </div>
        </div>
        <div className="p-4 position-relative overflow-hidden" style={{ backgroundColor: 'var(--bg-secondary)', borderRadius: '1rem', boxShadow: 'var(--card-shadow)', borderBottom: '4px solid #f59e0b' }}>
          <div className="position-absolute opacity-25" style={{ top: '-10px', right: '-10px', color: '#f59e0b' }}>
            <span className="material-symbols-outlined" style={{ fontSize: '80px' }}>science</span>
          </div>
          <div className="position-relative">
            <div style={{ fontSize: '0.7rem', fontWeight: 'bold', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '1px' }}>Demo Hesap</div>
            <div className="display-5 fw-bolder mt-1" style={{ color: 'var(--text-main)' }}>{stats.demo_hesap_sayisi || 0}</div>
          </div>
        </div>
        <div className="p-4 position-relative overflow-hidden" style={{ backgroundColor: 'var(--bg-secondary)', borderRadius: '1rem', boxShadow: 'var(--card-shadow)', borderBottom: '4px solid #ef4444' }}>
          <div className="position-absolute opacity-25" style={{ top: '-10px', right: '-10px', color: '#ef4444' }}>
            <span className="material-symbols-outlined" style={{ fontSize: '80px' }}>support_agent</span>
          </div>
          <div className="position-relative">
            <div style={{ fontSize: '0.7rem', fontWeight: 'bold', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '1px' }}>Açık Destek</div>
            <div className="display-5 fw-bolder mt-1" style={{ color: 'var(--text-main)' }}>
              {analitik?.ozet?.acik_ticket_sayisi ?? '—'}
            </div>
          </div>
        </div>
      </div>

      {/* Sistem Trendi / Analitik Grafik */}
      <div className="mb-5 p-4" style={{ backgroundColor: 'var(--bg-secondary)', borderRadius: '1rem', boxShadow: 'var(--card-shadow)' }}>
        <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
          <div className="d-flex align-items-center gap-2">
            <span className="material-symbols-outlined" style={{ color: 'var(--accent-primary)', fontSize: '24px' }}>trending_up</span>
            <h4 className="fw-bolder m-0" style={{ color: 'var(--text-main)' }}>Sistem Trendi</h4>
          </div>
          <div className="d-flex gap-2">
            <Button
              size="sm"
              variant={periyot === 7 ? 'primary' : 'outline-secondary'}
              onClick={() => setPeriyot(7)}
            >Son 7 Gün</Button>
            <Button
              size="sm"
              variant={periyot === 30 ? 'primary' : 'outline-secondary'}
              onClick={() => setPeriyot(30)}
            >Son 30 Gün</Button>
          </div>
        </div>

        {/* Özet sayaçlar */}
        {analitik?.ozet && (
          <div className="d-flex flex-wrap gap-4 mb-4">
            {[
              { label: 'Bu Hafta Yeni Kullanıcı', value: analitik.ozet.bu_hafta_yeni_kullanici, renk: '#6366f1' },
              { label: 'Bu Hafta Yeni Danışan', value: analitik.ozet.bu_hafta_yeni_danisan, renk: '#22c55e' },
              { label: 'Bu Hafta Randevu', value: analitik.ozet.bu_hafta_randevu, renk: '#f59e0b' },
              { label: 'Aktif Demo', value: analitik.ozet.aktif_demo_sayisi, renk: '#ef4444' },
            ].map(item => (
              <div key={item.label}>
                <div className="small" style={{ color: 'var(--text-muted)' }}>{item.label}</div>
                <div className="fw-bolder" style={{ fontSize: '1.5rem', color: item.renk }}>{item.value}</div>
              </div>
            ))}
          </div>
        )}

        {analitikYukleniyor ? (
          <div className="text-center py-5"><Spinner animation="border" size="sm" /></div>
        ) : analitik ? (
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={trendleriEslestir(analitik.kullanici_trend, analitik.danisan_trend, analitik.randevu_trend, periyot)}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--bg-tertiary)" />
              <XAxis dataKey="tarih" tick={{ fontSize: 11, fill: 'var(--text-muted)' }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: 'var(--text-muted)' }} />
              <Tooltip
                contentStyle={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--bg-tertiary)', borderRadius: 8 }}
                labelStyle={{ color: 'var(--text-main)', fontWeight: 'bold' }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="Yeni Kullanıcı" stroke="#6366f1" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="Yeni Danışan" stroke="#22c55e" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="Randevu" stroke="#f59e0b" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-muted text-center py-4">Analitik veriler yüklenemedi.</p>
        )}
      </div>

      {/* Karşılama Sayfası Trafiği */}
      <div className="mb-5 p-4" style={{ backgroundColor: 'var(--bg-secondary)', borderRadius: '1rem', boxShadow: 'var(--card-shadow)' }}>
        <div className="d-flex align-items-center gap-2 mb-4 flex-wrap">
          <span className="material-symbols-outlined" style={{ color: 'var(--accent-primary)', fontSize: '24px' }}>visibility</span>
          <h4 className="fw-bolder m-0" style={{ color: 'var(--text-main)' }}>Karşılama Sayfası Trafiği</h4>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>(giriş yapmış kullanıcılar sayılmaz)</span>
        </div>

        {trafik ? (
          <>
            <div className="d-flex flex-wrap gap-4 mb-4">
              {[
                { label: 'Toplam Görüntülenme', value: trafik.toplam_goruntuleme ?? 0, renk: '#60a5fa' },
                { label: 'Tekil Ziyaretçi (giren)', value: trafik.tekil_ziyaretci ?? 0, renk: '#22c55e' },
                { label: 'İnceleyen (kaydıran)', value: trafik.toplam_inceleme ?? 0, renk: '#f59e0b' },
                { label: 'İnceleme Oranı', value: `%${trafik.toplam_goruntuleme > 0 ? Math.round((trafik.toplam_inceleme / trafik.toplam_goruntuleme) * 100) : 0}`, renk: '#a78bfa' },
              ].map(item => (
                <div key={item.label}>
                  <div className="small" style={{ color: 'var(--text-muted)' }}>{item.label}</div>
                  <div className="fw-bolder" style={{ fontSize: '1.5rem', color: item.renk }}>{item.value}</div>
                </div>
              ))}
            </div>

            {(trafik.son_30_gun || []).length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={(trafik.son_30_gun || []).map(g => ({
                  tarih: (g.tarih || '').slice(5),
                  'Görüntülenme': g.goruntuleme,
                  'Tekil': g.tekil,
                  'İnceleme': g.inceleme,
                }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--bg-tertiary)" />
                  <XAxis dataKey="tarih" tick={{ fontSize: 11, fill: 'var(--text-muted)' }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: 'var(--text-muted)' }} />
                  <Tooltip
                    contentStyle={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--bg-tertiary)', borderRadius: 8 }}
                    labelStyle={{ color: 'var(--text-main)', fontWeight: 'bold' }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="Görüntülenme" stroke="#60a5fa" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="Tekil" stroke="#22c55e" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="İnceleme" stroke="#f59e0b" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-muted text-center py-4 m-0">Henüz ziyaret kaydı yok.</p>
            )}
          </>
        ) : (
          <p className="text-muted text-center py-4 m-0">Trafik verileri yüklenemedi.</p>
        )}
      </div>

      {/* Demo Hesaplar Tablosu */}
      {demoHesaplar.length > 0 && (
        <div className="p-4" style={{ backgroundColor: 'var(--bg-secondary)', borderRadius: '1rem', boxShadow: 'var(--card-shadow)' }}>
          <div className="d-flex align-items-center justify-content-between mb-4">
            <div className="d-flex align-items-center gap-2">
              <span className="material-symbols-outlined" style={{ color: '#f59e0b', fontSize: '24px' }}>science</span>
              <h4 className="fw-bolder m-0" style={{ color: 'var(--text-main)' }}>Demo Hesaplar</h4>
            </div>
            <Badge bg="warning" text="dark" pill className="px-3">{demoHesaplar.length} aktif demo</Badge>
          </div>

          <div className="table-responsive">
            <table className="w-100" style={{ borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--bg-tertiary)' }}>
                  <th className="pb-3 text-uppercase" style={{ fontSize: '0.65rem', letterSpacing: '1px', color: 'var(--text-muted)' }}>Kullanıcı</th>
                  <th className="pb-3 text-uppercase" style={{ fontSize: '0.65rem', letterSpacing: '1px', color: 'var(--text-muted)' }}>E-posta</th>
                  <th className="pb-3 text-uppercase text-center" style={{ fontSize: '0.65rem', letterSpacing: '1px', color: 'var(--text-muted)' }}>Danışan</th>
                  <th className="pb-3 text-uppercase text-center" style={{ fontSize: '0.65rem', letterSpacing: '1px', color: 'var(--text-muted)' }}>Randevu</th>
                  <th className="pb-3 text-uppercase text-center" style={{ fontSize: '0.65rem', letterSpacing: '1px', color: 'var(--text-muted)' }}>Kalan Gün</th>
                  <th className="pb-3 text-uppercase text-end" style={{ fontSize: '0.65rem', letterSpacing: '1px', color: 'var(--text-muted)' }}>İşlem</th>
                </tr>
              </thead>
              <tbody>
                {demoHesaplar.map(h => (
                  <tr key={h.id} className="border-bottom" style={{ borderColor: 'var(--bg-tertiary)' }}>
                    <td className="py-3">
                      <div className="fw-bold" style={{ color: 'var(--text-main)', fontSize: '0.9rem' }}>{h.kullanici_adi}</div>
                    </td>
                    <td className="py-3" style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>{h.eposta}</td>
                    <td className="py-3 text-center" style={{ color: 'var(--text-main)' }}>{h.danisan_sayisi}/5</td>
                    <td className="py-3 text-center" style={{ color: 'var(--text-main)' }}>{h.randevu_sayisi}/5</td>
                    <td className="py-3 text-center">
                      <Badge 
                        bg={h.kalan_gun <= 0 ? 'danger' : h.kalan_gun <= 3 ? 'warning' : 'success'} 
                        text={h.kalan_gun <= 3 && h.kalan_gun > 0 ? 'dark' : 'light'}
                        className="px-3 py-2 rounded-pill"
                      >
                        {h.kalan_gun <= 0 ? 'Süresi Doldu' : `${h.kalan_gun} gün`}
                      </Badge>
                    </td>
                    <td className="py-3 text-end">
                      <Button size="sm" className="fw-bold" style={{ background: 'linear-gradient(135deg, #6de039, #266d00)', border: 'none', fontSize: '0.75rem' }} onClick={() => handleDemoOnayla(h.id)}>
                        Normal Hesaba Dönüştür
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
export default AdminDashboard;